import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { cardToDeleteStore } from "features/CardToDelete";
import { filteredCardsStore } from "features/Filter";
import { LoadMoreButton } from "features/LoadMoreButton";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers } from "features/Panel";
import { createSeaUpdateCardHook, useSeaCardActions } from "features/SeaCardActions";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { useTagsContext } from "features/Tags";
import { tagsStore } from "features/Tags";
import { authStore } from "entities/Auth";
import { Card, LoadCard } from "entities/Card";
import { PanelView } from "entities/PanelView";
import { searchFilterStore } from "entities/SearchFilter";
import {
  isSeaSavedShipmentModel,
  RequestFuncParams,
  SeaSavedCard,
  useSeaInfiniteScrollFetch,
} from "entities/SeaSavedCard";
import { CARDS_REQUEST_LIMIT } from "shared/constants/common.constants";
import { savedShipmentLoadingText } from "shared/constants/panel.constants";
import { useAppSelector } from "shared/lib/redux";
import { Nullable } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";

type CardsByTagViewProps = {
  className?: string;
};

const GET_CARDS_BY_TAG_INFINITE_QUERY_KEY = "get_saved_cards_by_tag_infinite_query";

export const SeaCardsByTagView: RFC<CardsByTagViewProps> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const { showMainView, setCardsByTagViewHistory, currentHistoryState } = usePanelViews();
  const { activeTag, setActiveTag, refreshTags } = useTagsContext();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const cardsByTag = useAppSelector(tagsStore.selectors.cardsByTags);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);

  const seaCardsByTag = useMemo(() => {
    return cardsByTag.filter((card) => isSeaSavedShipmentModel(card));
  }, [cardsByTag]);

  const getFilterRequestParams = (): Nullable<Partial<RequestFuncParams>> => {
    if (!activeTag) return null;
    return {
      where: { tags: [activeTag] },
    };
  };

  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });

  const { deleteSeaCardHandler, isSeaCardDeleting } = useSeaCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
    removeFilteredCardAction: (card) => ({
      type: filteredCardsStore.actions.removeFilteredCardAction.type,
      payload: card,
    }),
    removeCardByTagAction: (card) => ({
      type: tagsStore.actions.removeCardByTagAction.type,
      payload: card,
    }),
    refreshTags,
  });

  const {
    cancelGetCardsRequest,
    resetSeaCardsQuery,
    shouldRenderSeaInfiniteQueryLoader,
    shouldRenderSeaMoreButton,
    requestSeaMoreCards,
  } = useSeaInfiniteScrollFetch<HTMLDivElement>({
    containerRef,
    onSuccess: ({ page }) => {
      dispatch(tagsStore.actions.appendCardsByTagAction({ data: page }));
    },
    onSettled: ({ totalCount }) => {
      setCardsByTagViewHistory({ ...getFilterRequestParams(), totalCount });
    },
    shouldRequestOnMount: () => {
      if (!isAuthenticated || !activeTag) return false;

      const baseCondition = seaCardsByTag.length < CARDS_REQUEST_LIMIT;

      if (currentHistoryState?.totalCount) {
        return baseCondition && currentHistoryState.totalCount > CARDS_REQUEST_LIMIT;
      }

      return baseCondition;
    },
    queryKey: [GET_CARDS_BY_TAG_INFINITE_QUERY_KEY],
    initialFilter: getFilterRequestParams(),
  });

  useEffect(() => {
    if (currentHistoryState?.totalCount && seaCardsByTag.length === 0) {
      setActiveTag(null);
      showMainView();
    }
  }, [seaCardsByTag.length]);

  useEffect(() => {
    if (!activeTag) {
      showMainView();
    }
  }, [activeTag]);

  useEffect(
    () => () => {
      resetSeaCardsQuery();
      cancelGetCardsRequest();
    },
    []
  );

  return (
    <>
      <PanelView
        ref={containerRef}
        className={className}
        shouldRenderMoreButton={shouldRenderSeaMoreButton}
        loadMoreButton={<LoadMoreButton onClick={requestSeaMoreCards} />}
      >
        {isAuthenticated && (
          <>
            {seaCardsByTag.map((card) => (
              <SeaSavedCard
                key={card.reference}
                data={card}
                openCard={openCard}
                isSavedCardDeleting={isSeaCardDeleting}
                useSavedCardUpdateActions={createSeaUpdateCardHook({
                  model: card,
                  isOpenedCardExist: !!openedCard,
                  openedCardUpdateAction: (response) => ({
                    type: openedCardStore.actions.updateOpenCardAction.type,
                    payload: response,
                  }),
                  updateFilteredCards: (response) => ({
                    type: filteredCardsStore.actions.updateFilterCardAction.type,
                    payload: response,
                  }),
                  updateCardByTagAction: (response) => ({
                    type: tagsStore.actions.updateCardByTagAction.type,
                    payload: response,
                  }),
                })}
                deleteCardHandler={deleteSeaCardHandler}
                shipmentType={shipmentType}
                renderCard={(props) => <Card {...props} />}
              />
            ))}
          </>
        )}
        {shouldRenderSeaInfiniteQueryLoader && <LoadCard text={t(savedShipmentLoadingText)} />}
      </PanelView>
    </>
  );
};
